import React, { ReactNode, useCallback, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import OverlayTrigger, {
  OverlayDelay,
  OverlayTriggerProps,
} from 'react-bootstrap/OverlayTrigger'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import Popover from 'react-bootstrap/Popover'
import palette from 'src/designSystem/variables/palette'

const StyledPopover = styled(Popover)<{
  hideArrow?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}>`
  max-width: 550px;
  .popover-body {
    background-color: ${palette.neutral.black};
    color: ${palette.neutral.white};
    border-radius: 4px;
  }

  ${(props) =>
    props.hideArrow &&
    `
    .popover-arrow {
      display: none;
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'top' &&
    `
    .popover-arrow::after {
      border-top-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'bottom' &&
    `
    .popover-arrow::after {
      border-bottom-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'left' &&
    `
    .popover-arrow::after {
      border-left-color: ${palette.neutral.black};
    }
  `}

  ${(props) =>
    !props.hideArrow &&
    props.placement === 'right' &&
    `
    .popover-arrow::after {
      border-right-color: ${palette.neutral.black};
    }
  `}
`

const defaultTooltipDelay: OverlayDelay = { show: 150, hide: 150 }

// More options: https://mui.com/material-ui/api/tooltip/#props
export enum TooltipPositions {
  default = 'auto',
  top = 'top',
  bottom = 'bottom',
  left = 'left',
  right = 'right',
}

export enum TooltipOverlayType {
  tooltip = 'tooltip',
  popover = 'popover',
}

export enum TOOLTIP_TRIGGERS {
  HOVER = 'hover',
  FOCUS = 'focus',
}

export const Tooltip = ({
  id,
  trigger,
  defaultShow,
  show,
  onToggle,
  delay = defaultTooltipDelay,
  placement = TooltipPositions.default,
  overlayType = TooltipOverlayType.tooltip,
  tooltipBody,
  children,
  disabled = false,
  width,
  hideArrow = false,
  keepOpenOnHover = false,
}: Omit<OverlayTriggerProps, 'overlay'> & {
  id: string
  overlayType?: TooltipOverlayType
  tooltipBody: ReactNode
  disabled?: boolean
  width?: number
  hideArrow?: boolean
  keepOpenOnHover?: boolean
}): JSX.Element => {
  const [isHoverControlled, setIsHoverControlled] = useState(false)
  const [internalShow, setInternalShow] = useState(false)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = useCallback(() => {
    if (keepOpenOnHover && hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [keepOpenOnHover])

  const handleMouseLeave = useCallback(() => {
    if (keepOpenOnHover) {
      hideTimeoutRef.current = setTimeout(() => {
        setInternalShow(false)
        setIsHoverControlled(false)
        onToggle?.(false)
      }, 150) // Small delay to allow moving to tooltip content
    }
  }, [keepOpenOnHover, onToggle])

  const handleTooltipMouseEnter = useCallback(() => {
    if (keepOpenOnHover && hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [keepOpenOnHover])

  const handleTooltipMouseLeave = useCallback(() => {
    if (keepOpenOnHover) {
      setInternalShow(false)
      setIsHoverControlled(false)
      onToggle?.(false)
    }
  }, [keepOpenOnHover, onToggle])

  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (keepOpenOnHover && nextShow) {
        setInternalShow(true)
        setIsHoverControlled(true)
      }
      onToggle?.(nextShow)
    },
    [keepOpenOnHover, onToggle],
  )

  const effectiveShow =
    keepOpenOnHover && isHoverControlled ? internalShow : show

  const overlay = disabled ? (
    <></>
  ) : overlayType === TooltipOverlayType.tooltip ? (
    <BootstrapTooltip id={id} style={{ width: width }}>
      {tooltipBody}
    </BootstrapTooltip>
  ) : (
    <StyledPopover
      id={id}
      style={{ width: width }}
      hideArrow={hideArrow}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <Popover.Body>{tooltipBody}</Popover.Body>
    </StyledPopover>
  )

  return (
    <OverlayTrigger
      popperConfig={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
      trigger={trigger}
      defaultShow={defaultShow}
      show={effectiveShow}
      onToggle={handleToggle}
      delay={delay}
      placement={placement}
      overlay={overlay}
    >
      {keepOpenOnHover
        ? React.cloneElement(children as React.ReactElement, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          })
        : children}
    </OverlayTrigger>
  )
}
